/**
 * SM Tidio capture - storefront
 * v1.1 (Sep 5 2026 - unload flush, durable pending queue, guarded delay)
 *
 * v1.1 changes. WHY: v1.0 lost any chat whose visitor left immediately.
 * Diagnosed Sep 5 2026 against two real misses. Both message triggers ran
 * through setTimeout(send, 1200) and nothing else ever fired, so if the
 * page was closed, backgrounded or frozen inside that window the callback
 * was dropped and no POST was ever made. On iOS a backgrounded tab freezes
 * timers almost at once, so a guest who typed and put the phone down was
 * lost with no trace anywhere in the stack. The visitor who stayed and kept
 * talking landed, because each new message re-triggered the send. That is
 * the whole intermittency: guests who stay land, guests who leave vanish.
 *
 *   - UNLOAD FLUSH. pagehide, and visibilitychange to hidden, both flush
 *     IMMEDIATELY, skipping SEND_DELAY_MS. pagehide is the only event iOS
 *     Safari reliably fires before a tab dies; unload and beforeunload are
 *     not fired there at all. TRADEOFF, deliberate: flushing on hide can
 *     ship a transcript missing the last message, because Tidio may not
 *     have written it to localStorage yet. The worker dedupes on
 *     idFromServer, so a later send from the same visitor corrects it. A
 *     partial transcript beats the nothing v1.0 produced.
 *   - DURABLE PENDING QUEUE. The body is written to localStorage BEFORE
 *     the fetch and cleared only on a 2xx. A send that fails, or that is
 *     killed mid-flight by the page unloading, is retried on the next page
 *     load, the next message, and the next flush. v1.0's .catch(){} dropped
 *     failures silently with no retry, and the only recovery path was the
 *     visitor loading another page, which a leaving visitor does not do.
 *   - GUARDED DELAY. The 1200ms wait still exists (Tidio writes the message
 *     to localStorage after the event fires, so sending at once posts a
 *     transcript missing its trigger), but the pending timer is tracked and
 *     any flush cancels it and sends now. The delay can no longer swallow
 *     a send.
 *   - sendBeacon on the unload path when the payload fits, since keepalive
 *     fetch is not honored during unload on every iOS version. Falls back
 *     to keepalive fetch. Beacon cannot read the response, so the pending
 *     record is left in place and cleared by the next successful fetch;
 *     the worker's idFromServer dedupe makes the duplicate harmless.
 *   - RETRY BUDGET. A pending record older than PENDING_MAX_AGE_MS or past
 *     PENDING_MAX_ATTEMPTS is dropped, so a permanently failing endpoint
 *     cannot wedge a visitor's browser into retrying forever.
 *
 * v1.0 (Sep 2026)
 *
 * Reads Tidio's own localStorage state and POSTs it to sm-tidio-worker,
 * which assembles the CSV and panel blobs and hands them to sm-onboard
 * /api/capture. Replaces the manual CSV paste into the 360 Onboard tab.
 *
 * RUNS ON EVERY PAGE, deliberately. The page-view log has to start at
 * first touch, because Tidio's own Viewed Pages list covers the whole
 * visit and the browse history feeds hero and picks extraction. A script
 * that only loaded when a chat opened would produce a one-entry history.
 *
 * SENDS NOTHING until the visitor has actually typed. No contact id, or
 * no visitor-authored message, means no POST. Browsing alone is logged
 * locally and never leaves the browser.
 *
 * TIME: message times come from Tidio's time_sent (epoch seconds); page
 * views use Date.now(). Both are UTC instants. No formatted date and no
 * local wall-clock string is ever sent. The worker renders Eastern only
 * at the last moment, because the upstream parsers expect Tidio's format.
 *
 * KNOWN GAP, NOT FIXABLE HERE. Operator messages are only captured while
 * the guest's browser is open. Once the visitor leaves, anything an
 * operator types into the Tidio panel reaches nothing. Closing that needs
 * a server-side source (Tidio webhook or API), not this script.
 *
 * FAILS SILENT. Every path is wrapped. A capture failure must never
 * affect the storefront.
 */
(function () {
  'use strict';

  var INGEST = 'https://sm-tidio.michaelryanwright.workers.dev/ingest';
  var PAGEVIEW_KEY = 'sm_pageviews';
  var SENT_KEY = 'sm_last_sent';
  var PENDING_KEY = 'sm_pending_send';
  var PAGEVIEW_CAP = 200;
  var SEND_DELAY_MS = 1200;

  // A pending send that cannot succeed must not follow a visitor around
  // forever. Two hours covers a browser reopened later in the session;
  // beyond that the conversation is stale and capture would slice it away.
  var PENDING_MAX_AGE_MS = 2 * 60 * 60 * 1000;
  var PENDING_MAX_ATTEMPTS = 8;

  // sendBeacon queues are small and a rejected beacon returns false, which
  // we cannot distinguish from a lost one after unload. Stay well under.
  var BEACON_MAX_BYTES = 60000;

  var delayTimer = null;
  var inFlight = false;

  function findState() {
    try {
      var key = Object.keys(localStorage).find(function (k) {
        return /^tidio_state_[a-z0-9]+$/i.test(k);
      });
      if (!key) return null;
      return JSON.parse(localStorage.getItem(key));
    } catch (e) { return null; }
  }

  function readLog() {
    try { return JSON.parse(localStorage.getItem(PAGEVIEW_KEY)) || []; }
    catch (e) { return []; }
  }

  function logPageView() {
    try {
      var log = readLog();
      var last = log[log.length - 1];
      if (!last || last.url !== location.href) {
        log.push({ url: location.href, ts: Date.now(), ref: document.referrer || '' });
      }
      while (log.length > PAGEVIEW_CAP) log.shift();
      localStorage.setItem(PAGEVIEW_KEY, JSON.stringify(log));
      return log;
    } catch (e) { return []; }
  }

  function operatorNameFor(state, msg) {
    var list = state.assignedOperatorsData || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === msg.operator_id) return list[i].name || null;
    }
    return null;
  }

  function buildBody(state, log) {
    var v = state.visitor || {};
    var messages = (state.messages || []).filter(function (m) {
      return m && m.content && m.time_sent;
    }).map(function (m) {
      return {
        idFromServer: m.idFromServer != null ? m.idFromServer : null,
        timeSent: m.time_sent * 1000,
        sender: m.sender,
        content: m.content,
        botName: m.chat_bot_name || null,
        operatorName: m.sender === 'operator' ? operatorNameFor(state, m) : null
      };
    });

    return {
      contactId: v.id || '',
      visitor: {
        email: v.email || '',
        phone: v.phone || '',
        browser: v.browser || '',
        browserVersion: v.browser_version || '',
        osName: v.os_name || ''
      },
      messages: messages,
      pageViews: log
    };
  }

  // Without this, every page load re-posts an identical session and
  // re-arms the quiet-window alarm, so a visitor who finishes a chat and
  // keeps browsing would hold that session open indefinitely.
  function fingerprint(body) {
    var last = body.messages[body.messages.length - 1];
    return [body.contactId, body.messages.length, last ? last.timeSent : 0].join(':');
  }

  // ----- pending queue -------------------------------------------------
  // The record is written before the request leaves and cleared only on a
  // 2xx, so a send interrupted by the page dying is retried rather than
  // lost. This is what makes a leaving visitor recoverable.

  function readPending() {
    try {
      var raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return null;
      var p = JSON.parse(raw);
      if (!p || !p.body || !p.fp) return null;
      if (Date.now() - (p.createdMs || 0) > PENDING_MAX_AGE_MS) {
        localStorage.removeItem(PENDING_KEY);
        return null;
      }
      if ((p.attempts || 0) >= PENDING_MAX_ATTEMPTS) {
        localStorage.removeItem(PENDING_KEY);
        return null;
      }
      return p;
    } catch (e) { return null; }
  }

  function writePending(body, fp) {
    try {
      var existing = readPending();
      localStorage.setItem(PENDING_KEY, JSON.stringify({
        body: body,
        fp: fp,
        createdMs: (existing && existing.fp === fp && existing.createdMs) || Date.now(),
        attempts: (existing && existing.fp === fp ? (existing.attempts || 0) : 0) + 1
      }));
    } catch (e) {}
  }

  function clearPending(fp) {
    try {
      var p = readPending();
      if (!p || p.fp === fp) localStorage.removeItem(PENDING_KEY);
    } catch (e) {}
  }

  function markSent(fp) {
    try { localStorage.setItem(SENT_KEY, fp); } catch (e) {}
    clearPending(fp);
  }

  // ----- transport -----------------------------------------------------

  function post(body, fp, viaBeacon) {
    var payload = JSON.stringify(body);

    // Unload path. keepalive fetch is not honored on every iOS version
    // during unload, and sendBeacon is. Beacon gives us no response, so
    // the pending record stays and the next successful fetch clears it.
    // A duplicate is harmless: the worker dedupes on idFromServer.
    if (viaBeacon && navigator.sendBeacon && payload.length <= BEACON_MAX_BYTES) {
      try {
        var blob = new Blob([payload], { type: 'application/json' });
        if (navigator.sendBeacon(INGEST, blob)) return;
      } catch (e) {}
    }

    inFlight = true;
    fetch(INGEST, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true
    }).then(function (res) {
      inFlight = false;
      if (res.ok) markSent(fp);
    }).catch(function () {
      inFlight = false;
      // Left pending on purpose. Retried on next load, message or flush.
    });
  }

  // Retry whatever the last attempt failed to deliver, before building a
  // fresh body. Runs on page load so a visitor who returns closes their
  // own gap without doing anything.
  function flushPending(viaBeacon) {
    var p = readPending();
    if (!p) return;
    if (localStorage.getItem(SENT_KEY) === p.fp) { clearPending(p.fp); return; }
    writePending(p.body, p.fp);
    post(p.body, p.fp, viaBeacon);
  }

  function send(reason, viaBeacon) {
    try {
      if (delayTimer) { clearTimeout(delayTimer); delayTimer = null; }

      var state = findState();
      if (!state) return;

      var body = buildBody(state, readLog());
      if (!body.contactId) return;

      var spoke = body.messages.some(function (m) { return m.sender === 'visitor'; });
      if (!spoke) return;

      var fp = fingerprint(body);
      if (localStorage.getItem(SENT_KEY) === fp) { clearPending(fp); return; }

      writePending(body, fp);
      post(body, fp, viaBeacon);
    } catch (e) {}
  }

  // Any flush cancels the pending delay and goes now. v1.0's bare
  // setTimeout could be dropped by the page freezing; this cannot.
  function flushNow(reason) {
    try {
      if (delayTimer) { clearTimeout(delayTimer); delayTimer = null; }
      if (!inFlight) flushPending(true);
      send(reason, true);
    } catch (e) {}
  }

  function scheduleSend(reason) {
    if (delayTimer) clearTimeout(delayTimer);
    delayTimer = setTimeout(function () {
      delayTimer = null;
      send(reason, false);
    }, SEND_DELAY_MS);
  }

  function arm() {
    try {
      var api = window.tidioChatApi;
      if (!api || arm.done) return;
      arm.done = true;
      // The event fires on delivery; Tidio writes the message into
      // localStorage a moment later. Sending immediately would post a
      // transcript missing the message that triggered it.
      ['messageFromVisitor', 'messageFromOperator'].forEach(function (ev) {
        api.on(ev, function () { scheduleSend(ev); });
      });
    } catch (e) {}
  }

  logPageView();

  // The widget is injected by the Tidio Shopify app, so load order is not
  // ours to control. Handle both cases.
  if (window.tidioChatApi) arm();
  else document.addEventListener('tidioChat-ready', arm);

  // pagehide is the one event iOS Safari fires reliably before a tab is
  // discarded; unload and beforeunload are not fired there. visibilitychange
  // to hidden covers app-switching, which on iOS freezes timers at once and
  // is the exact case that lost the 1:06 AM chat.
  window.addEventListener('pagehide', function () { flushNow('pagehide'); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushNow('hidden');
  });

  // Catches a conversation already in progress on a fresh page load, and
  // retries anything the previous page failed to deliver.
  flushPending(false);
  send('pageload', false);
})();